import _ from 'lodash';
import moment from 'moment-timezone';
import {
  Appointment,
  AppointmentConcern,
  BookingFlowOption,
  Physician,
  PhysicianPhysicianTypeEnum,
  Subscription,
} from 'generated/api';
import AppointmentUtils from './AppointmentUtils';

/**
 * PPU -> subscription conversion.
 *
 * Both the "PPU Conversion Rates" chart (Clinic section) and the "PPU to Sub
 * Conversion Rate" row (Financials reporting) are built from here so the two
 * always report the same number.
 *
 * The denominator is a completed, paid New Health Concern visit with an NP —
 * infusions, labs, procedures and the specialty programs are paid too, but
 * they aren't the visit that turns someone into a member.
 */

/** A subscription only counts as a conversion when it starts this soon after the visit. */
export const CONVERSION_WINDOW = 30 * 24 * 60 * 60;

export const MONTH_FORMAT = 'YYYY-MM';

/**
 * The same concern is defined once per booking flow (Video, Voice, Clinic,
 * Home), and its text has drifted in casing/pluralization over the years, so
 * match on the normalized name rather than a single id or an exact string.
 */
export function isNewHealthConcern(concern: AppointmentConcern) {
  return /^new health concerns?(\(s\))?$/i.test((concern.text ?? '').trim());
}

export function getNewHealthConcernIds(options: BookingFlowOption[] = []) {
  const concerns = _.compact(_.flattenDeep(options.map((option) => option.concerns ?? [])));
  return new Set(
    concerns.filter((concern) => isNewHealthConcern(concern)).map((concern) => concern.id!),
  );
}

export function isPPUConversionAppointment(
  appointment: Appointment,
  newConcernIds: Set<string>,
  physicians: Record<string, Physician>,
  clinicId?: string,
) {
  const { status, fee, concern_id, physician_id } = appointment;
  if (status !== AppointmentUtils.getAppointmentStatus().completed) return false;
  if (!fee) return false;
  if (!newConcernIds.has(concern_id!)) return false;

  const physician = physicians[physician_id!];
  if (!physician) return false;
  if (clinicId && physician.clinic_id !== clinicId) return false;

  const type = physician.physician_type;
  return !type || type === PhysicianPhysicianTypeEnum.NursePractitioner;
}

export type MonthlyConversions = Record<
  string,
  { appointments: Set<string>; conversions: Set<string> }
>;

/**
 * Buckets patients by month: the visit month for the denominator, the
 * subscription month for the numerator (a patient seen in January who
 * subscribes in February converts in February).
 *
 * `appointments` must already be narrowed with isPPUConversionAppointment.
 */
export function getConversionsByMonth(
  appointments: Appointment[],
  subscriptions: Subscription[] = [],
  format = MONTH_FORMAT,
): MonthlyConversions {
  const byMonth: MonthlyConversions = {};
  const bucket = (month: string) => {
    if (!byMonth[month]) byMonth[month] = { appointments: new Set(), conversions: new Set() };
    return byMonth[month]!;
  };

  appointments.forEach((appointment) => {
    bucket(moment.unix(appointment.date!).format(format)).appointments.add(appointment.patient_id!);

    const subscription = subscriptions.find(
      ({ patient_id, created }) =>
        patient_id === appointment.patient_id &&
        created! > appointment.date! &&
        created! <= appointment.date! + CONVERSION_WINDOW,
    );

    if (subscription) {
      bucket(moment.unix(subscription.created!).format(format)).conversions.add(
        appointment.patient_id!,
      );
    }
  });

  return byMonth;
}

/** Rate over a set of months, weighted by volume — not an average of monthly rates. */
export function weightedRate(months: { appointments: number; conversions: number }[]) {
  const appointments = _.sumBy(months, 'appointments');
  const conversions = _.sumBy(months, 'conversions');
  return appointments > 0 ? (conversions / appointments) * 100.0 : null;
}

/**
 * The financials reporting shape: a monthly percentage series plus the two
 * summary columns. The current month is left out — its conversion window
 * hasn't elapsed, so it always reads low.
 */
export function getConversionSummary(
  appointments: Appointment[],
  subscriptions: Subscription[] = [],
) {
  const byMonth = getConversionsByMonth(appointments, subscriptions);
  // YYYY-MM sorts and compares lexicographically.
  const currentMonth = moment().format(MONTH_FORMAT);
  const lastMonth = moment().subtract(1, 'month').format(MONTH_FORMAT);
  const twelveMonthsAgo = moment().subtract(12, 'months').format(MONTH_FORMAT);

  const entries = Object.keys(byMonth)
    .filter((month) => month < currentMonth)
    .sort()
    .map((month) => ({
      month,
      appointments: byMonth[month]!.appointments.size,
      conversions: byMonth[month]!.conversions.size,
    }));

  const months: Record<string, number> = {};
  entries.forEach(({ month, appointments, conversions }) => {
    // A month can hold conversions from the previous month's visits without
    // any visits of its own — countable in a total, but not a rate.
    if (appointments > 0) months[month] = (conversions / appointments) * 100.0;
  });

  return {
    months,
    thisYear: weightedRate(entries.filter(({ month }) => month > twelveMonthsAgo)),
    lastMonth: months[lastMonth] ?? null,
  };
}
